import { Candidate } from "@/types/candidate";
import { UserCircle, Circle, FileText, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SkillBadge } from "@/components/SkillBadge";
import { EntityList } from "@/components/common/EntityList";
import { useTranslation } from "react-i18next";
import { useMultiSelect } from "@/hooks/useMultiSelect";
import { useCandidateStatus } from "@/utils/candidate/statusUtils";
import { useState, useEffect, useMemo } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DuplicateManager, useDuplicateManager } from "@/components/common";
import { candidateMergeConfig, CandidateForMerge } from "@/config/candidateMergeConfig";
import { EntityMergeService } from "@/services/entityMergeService";
import { toast } from "@/hooks/use-toast";
import { CVFullScreenModal } from "./CVFullScreenModal";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { CandidateMassUpdateModal } from "./CandidateMassUpdateModal";

interface CVActionButtonProps {
  candidate: Candidate;
  isLoading: boolean;
  onPreviewCV: (candidate: Candidate) => void;
  hasCV: boolean;
}

const CVActionButton = ({ candidate, isLoading, onPreviewCV, hasCV }: CVActionButtonProps) => {
  const { t } = useTranslation();

  if (!hasCV) {
    return (
      <span className="relative inline-block h-5 w-5">
        <FileText className="h-5 w-5 text-gray-300 dark:text-gray-600" />
        <svg className="absolute inset-0 h-5 w-5 text-gray-400 dark:text-gray-500" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="4" x2="16" y2="16" /></svg>
      </span>
    );
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => onPreviewCV(candidate)}
      disabled={isLoading}
      title={t("candidates.cv.viewCV", "View CV")}
    >
      <FileText className="h-5 w-5 text-blue-500" />
      {isLoading && <span className="ml-2 text-xs">{t("common.loading")}</span>}
    </Button>
  );
};

interface CandidatesListProps {
  candidates: Candidate[];
  filteredCandidates: Candidate[];
  isLoading: boolean;
  onDeleteCandidates?: (candidateIds: string[]) => Promise<void>;
  onCandidatesChange?: () => void;
  favorites?: Set<string>;
}

export const CandidatesList = ({
  candidates,
  filteredCandidates,
  isLoading,
  onDeleteCandidates,
  onCandidatesChange,
  favorites = new Set()
}: CandidatesListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getStatusColor, getLocalizedStatus } = useCandidateStatus();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [cvModal, setCvModal] = useState<{ url: string; name: string } | null>(null);
  const [loadingCVId, setLoadingCVId] = useState<string | null>(null);
  const [isMassUpdateModalOpen, setIsMassUpdateModalOpen] = useState(false);

  // Handle link clicks - only intercept regular clicks for React Router navigation
  // For modifier clicks (ctrl/cmd), let the browser handle it naturally via the href
  const handleCandidateLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // For modifier key clicks, let browser handle native <a> behavior
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0)
      return;
    
    // For regular clicks, use React Router
    e.preventDefault();
    const candidateId = e.currentTarget.getAttribute('data-candidate-id');
    if (candidateId)
      navigate(`/candidates/${candidateId}`);
  };

  const {
    isMultiSelectMode,
    toggleMultiSelect,
    toggleItemSelection,
    handleDeleteSelected,
    clearSelection,
    isSelected,
    selectedCount,
    selectAll
  } = useMultiSelect(filteredCandidates, onDeleteCandidates || (() => Promise.resolve()));

  const candidateFields = [
    "name",
    "email",
    "phone",
    "function",
    "skills",
    "match_score",
    "manager",
    "status",
    "ai_analysis",
    "ai_synthesis",
    "embedding",
    "linkedin",
    "linkedin_json",
    "location"
  ];

  function normalizeCandidate(candidate) {
    const normalized = { ...candidate };
    candidateFields.forEach(field => {
      if (!(field in normalized)) {
        normalized[field] = "";
      }
    });
    return normalized;
  }
  const normalizedCandidates = filteredCandidates.map(normalizeCandidate);

  const {
    openMergeModal,
    getDuplicateManagerProps
  } = useDuplicateManager(normalizedCandidates, isSelected, "candidates", candidateMergeConfig);
  // Handle candidate merge
  const handleMerge = async (merged: CandidateForMerge, sources: CandidateForMerge[]) => {
    try {
      const result = await EntityMergeService.mergeCandidates(merged, sources);
      if (result.success) {
        toast({
          title: "Candidates merged successfully",
          description: `${sources.length} candidates have been merged into one.`,
        });
        // Refresh candidates list
        if (onCandidatesChange) {
          onCandidatesChange();
        }
      } else {
        toast({
          title: "Failed to merge candidates",
          description: result.error || "An error occurred during merge.",
          variant: "destructive",
        });
        // Rethrow error to prevent modal from closing
        throw new Error(result.error || "An error occurred during merge.");
      }
      if (result.redirectPath)
        navigate(result.redirectPath);
      return result;
    } catch (error) {
      console.error("Merge error:", error);
      toast({
        title: "Failed to merge candidates",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
      // Rethrow error to prevent modal from closing
      throw error;
    }
  };

  // Mass update handlers
  const handleMassUpdate = () => {
    setIsMassUpdateModalOpen(true);
  };

  const handleMassUpdateComplete = () => {
    setIsMassUpdateModalOpen(false);
    clearSelection();
    onCandidatesChange?.();
  };

  const selectedItemIds = Array.from(
    new Set(
      filteredCandidates
        .filter(candidate => isSelected(candidate.id))
        .map(candidate => candidate.id)
    )
  );

  // CV preview logic
  const handlePreviewCV = async (candidate: Candidate) => {
    setLoadingCVId(candidate.id);
    try {
      // 1. Get the candidate's default_resume (storage_id)
      const { data: candidateData, error: candidateError } = await supabase
        .from('candidates')
        .select('default_resume')
        .eq('id', candidate.id)
        .single();
      if (candidateError || !candidateData?.default_resume) {
        toast({
          title: "No CV Available",
          description: `${candidate.name} doesn't have a CV uploaded yet.`,
          variant: "destructive"
        });
        return;
      }
      const storageId = candidateData.default_resume;
      // 2. Get the display name (optional)
      const { data: doc, error: docError } = await supabase
        .from('documents')
        .select('display_name, name')
        .eq('storage_id', storageId)
        .single();
      const displayName = doc?.display_name || doc?.name || "CV";
      // 3. Call the edge function using supabase.functions.invoke
      const { data, error } = await supabase.functions.invoke(`document-manager?command=get-document-url&documentStorageId=${storageId}`, {
        method: 'GET',
      });
      
      if (error) throw new Error(`Edge function error: ${error.message}`);
      if (!data?.signedUrl) throw new Error("No signed URL returned");
      
      let url = data.signedUrl;
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        url = url.replace('http://kong:8000', 'http://localhost:54321');
      }
      setCvModal({ url, name: displayName });
    } catch (err) {
      toast({
        title: "Error Loading CV",
        description: "Failed to load CV preview. Please try again.",
        variant: "destructive"
      });
      setCvModal(null);
    } finally {
      setLoadingCVId(null);
    }
  };

  const columns = [
    {
      key: "name",
      header: t("candidates.list.name"),
      cell: (candidate: Candidate) => (
        <a 
          href={`/candidates/${candidate.id}`}
          data-candidate-id={candidate.id}
          onClick={handleCandidateLinkClick}
          className="hover:underline flex items-center gap-2"
        >
          <UserCircle className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          {candidate.name}
          {favorites.has(candidate.id) && <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />}
        </a>
      ),
    },
    {
      key: "function",
      header: t("candidates.list.function"),
      cell: (candidate: Candidate) => candidate.function || "-",
    },
    {
      key: "email",
      header: t("candidates.list.email"),
      cell: (candidate: Candidate) => (
        <a href={`mailto:${candidate.email}`} className="text-blue-500 hover:underline">
          {candidate.email}
        </a>
      ),
    },
    {
      key: "skills",
      header: t("candidates.list.skills"),
      cell: (candidate: Candidate) => (
        <div className="flex flex-wrap gap-1">
          {candidate.skills && candidate.skills.slice(0, 3).map(skill => (
            <SkillBadge key={skill} label={skill} />
          ))}
          {candidate.skills && candidate.skills.length > 3 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">+{candidate.skills.length - 3} {t("candidates.list.more")}</span>
          )}
        </div>
      ),
    },
    {
      key: "status",
      header: t("candidates.list.status"),
      cell: (candidate: Candidate) => {
        const statusColor = getStatusColor(candidate.status);
        return (
          <div className="flex items-center gap-1.5">
            <Circle className={`h-3 w-3 fill-current ${statusColor}`} />
            <span>{getLocalizedStatus(candidate.status)}</span>
          </div>
        );
      },
    },
    {
      key: "actions",
      header: t("candidates.list.cv", "CV"),
      cell: (candidate: Candidate) => {
        const hasCV = candidate.default_resume !== null && candidate.default_resume !== undefined && candidate.default_resume !== '';
        return (
          <CVActionButton
            candidate={candidate}
            isLoading={loadingCVId === candidate.id}
            onPreviewCV={handlePreviewCV}
            hasCV={hasCV}
          />
        );
      },
    },
  ];

  return (
    <>
      <EntityList
        data={normalizedCandidates}
        columns={columns}
        isLoading={isLoading}
        emptyMessage={t("candidates.list.empty")}
        basePath="/candidates"
        isMultiSelectMode={isMultiSelectMode}
        onToggleMultiSelect={toggleMultiSelect}
        onToggleItemSelection={toggleItemSelection}
        isItemSelected={isSelected}
        onDeleteSelected={() => setShowDeleteDialog(true)}
        onMergeSelected={openMergeModal}
        onMassUpdate={handleMassUpdate}
        selectedCount={selectedCount}
        selectAll={selectAll}
        // Removed showCVIcon
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("common.deleteConfirmation", { count: selectedCount })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("common.deleteWarning")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleDeleteSelected();
                setShowDeleteDialog(false);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <DuplicateManager
        {...getDuplicateManagerProps(handleMerge)}
      />
      {cvModal && (
        <CVFullScreenModal
          cvUrl={cvModal.url}
          name={cvModal.name}
          onClose={() => setCvModal(null)}
        />
      )}
      <CandidateMassUpdateModal
        isOpen={isMassUpdateModalOpen}
        onClose={() => setIsMassUpdateModalOpen(false)}
        selectedCandidateIds={selectedItemIds}
        onUpdate={handleMassUpdateComplete}
      />
    </>
  );
};
